import { useState } from 'react';
import { useOp } from '../../api.ts';
import { useKeys } from '../../ui/keys.ts';
import { Empty, ErrorBox, Loading } from '../../ui/kit.tsx';

type Group = 'site' | 'merchant' | 'coupon' | 'placement' | 'network' | 'day';

interface RevenueRow {
  key: string;
  label: string;
  site: string | null;
  currency: string;
  clicks: number;
  orders: number;
  declinedOrders: number;
  codeConfirmedOrders: number;
  saleAmount: number;
  commissionPending: number;
  commissionApproved: number;
  earningsPerClick: number | null;
  conversionRate: number | null;
  isDemo: boolean;
}

interface RevenueResult {
  rows: RevenueRow[];
  totals: { currency: string; clicks: number; orders: number; commissionPending: number; commissionApproved: number; earningsPerClick: number | null }[];
  sites: { ref: string; name: string }[];
}

const GROUPS: { value: Group; label: string; key: string }[] = [
  { value: 'site', label: 'Site', key: '1' },
  { value: 'merchant', label: 'Merchant', key: '2' },
  { value: 'coupon', label: 'Coupon', key: '3' },
  { value: 'placement', label: 'Placement', key: '4' },
  { value: 'network', label: 'Network', key: '5' },
  { value: 'day', label: 'Day', key: '6' },
];

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

const isoDay = (date: Date) => date.toISOString().slice(0, 10);

function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

const percent = (value: number | null) => (value === null ? '–' : `${(value * 100).toFixed(1)}%`);

/** Clicks, orders and commission from network reports, per site and below; every row stays tied to one site. */
export function Revenue() {
  const [group, setGroup] = useState<Group>('site');
  const [days, setDays] = useState(30);
  const [siteRef, setSiteRef] = useState('');
  const to = isoDay(new Date());
  const from = isoDay(new Date(Date.now() - (days - 1) * 86_400_000));
  const report = useOp<RevenueResult>('admin.revenue.summary', { from, to, groupBy: group, ...(siteRef ? { propertyRef: siteRef } : {}) });

  useKeys(Object.fromEntries(GROUPS.map((g) => [g.key, () => setGroup(g.value)])), true);

  const data = report.data;
  const showSite = group !== 'site' && group !== 'day';
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Revenue</h1>
          <p className="muted">Clicks, orders and commission the networks report, matched to our clicks. Pending commission can still be declined; the last days fill in as networks report.</p>
        </div>
      </header>

      <div className="toolbar" role="group" aria-label="Report options">
        <label>
          Site
          <select id="revenue-site" value={siteRef} onChange={(e) => setSiteRef(e.target.value)}>
            <option value="">All my sites</option>
            {(data?.sites ?? []).map((site) => (
              <option key={site.ref} value={site.ref}>
                {site.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Period
          <select id="revenue-days" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {RANGES.map((range) => (
              <option key={range.days} value={range.days}>
                Last {range.label}
              </option>
            ))}
          </select>
        </label>
        <div className="segmented" role="group" aria-label="Group by">
          {GROUPS.map((g) => (
            <button key={g.value} type="button" aria-pressed={group === g.value} title={`Group by ${g.label.toLowerCase()} (${g.key})`} onClick={() => setGroup(g.value)}>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {report.error ? <ErrorBox error={report.error} onRetry={report.reload} /> : null}
      {!data && report.loading ? <Loading /> : null}

      {data && data.totals.length ? (
        <div className="revenue-totals" aria-label="Totals">
          {data.totals.map((t) => (
            <div key={t.currency} className="revenue-total">
              <strong>{money(t.commissionPending + t.commissionApproved, t.currency)}</strong>
              <span className="muted">
                {money(t.commissionApproved, t.currency)} approved · {t.orders} orders · {t.clicks} clicks · EPC {t.earningsPerClick === null ? '–' : money(t.earningsPerClick, t.currency)}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {data && data.rows.length === 0 ? <Empty>No clicks or sales in this period for your sites.</Empty> : null}
      {data && data.rows.length ? (
        <table className="table" aria-label={`Revenue by ${group}`}>
          <thead>
            <tr>
              <th scope="col">{GROUPS.find((g) => g.value === group)!.label}</th>
              {showSite ? <th scope="col">Site</th> : null}
              <th scope="col" className="num">Clicks</th>
              <th scope="col" className="num">Orders</th>
              <th scope="col" className="num">Conv.</th>
              <th scope="col" className="num">Pending</th>
              <th scope="col" className="num">Approved</th>
              <th scope="col" className="num">EPC</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={`${row.key}:${row.currency}`}>
                <td>
                  {row.label}
                  {row.isDemo ? <span className="badge badge-muted">demo</span> : null}
                  {row.codeConfirmedOrders > 0 ? <span className="muted"> · code confirmed in {row.codeConfirmedOrders}</span> : null}
                </td>
                {showSite ? <td className="muted">{row.site}</td> : null}
                <td className="num">{row.clicks}</td>
                <td className="num">
                  {row.orders}
                  {row.declinedOrders ? <span className="muted"> (+{row.declinedOrders} declined)</span> : null}
                </td>
                <td className="num">{percent(row.conversionRate)}</td>
                <td className="num">{money(row.commissionPending, row.currency)}</td>
                <td className="num">{money(row.commissionApproved, row.currency)}</td>
                <td className="num">{row.earningsPerClick === null ? '–' : money(row.earningsPerClick, row.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
